import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Palette, Text } from '@updater/ui-uds';
import { X, File } from '@phosphor-icons/react';
import { MessageList } from '../../message-list/MessageList';
import { InputBar } from '../../input-bar/InputBar';
import { ChatColors } from '../../../../configs/chat-colors';
import { type ChatInterfaceProps, type ChatInterfaceRef } from '../../../types';

const ChatContainer = styled(Box)`
  /* Full-height card container; scrolling handled by inner MessagesScrollArea */
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  text-align: left;
  overflow: hidden;
  backdrop-filter: blur(10px);
  /* Stable dimensions to prevent position shifts */
  width: 100%;
  min-width: 100%;
  position: relative;
  /* Prevent layout shifts during sidebar animations */
  transform: translateZ(0);
  will-change: auto;
  /* Isolate from external layout changes */
  contain: layout;

  &:focus-within {
    outline: none;
  }
`;

const MessagesScrollArea = styled(Box)`
  flex: 1;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  /* Prevent position shifts from external layout changes */
  position: relative;
  min-height: 0;
  min-width: 100%;
  width: 100%;
  /* Force hardware acceleration and prevent reflow during animations */
  transform: translateZ(0);
  will-change: auto;
  /* Isolate from parent layout changes */
  contain: layout style;
`;

const FileListContainer = styled(Box)`
  padding: 0.5rem 1.5rem;
  border-top: ${ChatColors.filelist.BORDER};
  background-color: ${ChatColors.filelist.BG};
`;

const StickyInputBarWrapper = styled(Box)`
  position: sticky;
  bottom: 0;
  z-index: 20;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: ${ChatColors.filelist.INPUTBAR_GRADIENT};
  display: block;
`;

// Chip-style preview row
const FileChipsRow = styled(Flex)`
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FileChip = styled(Flex)`
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 9999px;
  background-color: ${ChatColors.filechip.BG};
  border: ${ChatColors.filechip.BORDER};
  color: ${ChatColors.filechip.TEXT};
  font-size: 0.875rem;
  line-height: 1;
`;

const ChipRemoveButton = styled.button`
  background: ${ChatColors.filechip.REMOVE_BG};
  border: none;
  color: ${ChatColors.filechip.TEXT};
  cursor: pointer;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${ChatColors.filechip.REMOVE_HOVER_BG};
  }
`;

const ChatInterfaceComponent = React.forwardRef<
  ChatInterfaceRef,
  ChatInterfaceProps
>(
  (
    {
      messages = [],
      placeholder = 'Type your message...',
      onSendMessage,
      isLoading = false,
      disabled = false,
      className,
      // onOpenVoiceAgent,
      enableScrollBehavior = true,
      animateLastAssistant = false,
      onTypewriterDone,
    }: ChatInterfaceProps,
    ref: React.Ref<ChatInterfaceRef>
  ) => {
    // Ref for scroll area and end marker
    const messagesScrollAreaRef = React.useRef<HTMLDivElement>(null);
    const endRef = React.useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    React.useEffect(() => {
      if (!enableScrollBehavior || messages.length === 0) return;

      const scrollToBottom = (
        behavior: 'auto' | 'smooth' = 'auto',
        retries = 3
      ) => {
        const performScroll = () => {
          if (messagesScrollAreaRef.current) {
            const element = messagesScrollAreaRef.current;
            const scrollHeight = element.scrollHeight;
            const clientHeight = element.clientHeight;

            if (scrollHeight > clientHeight) {
              element.scrollTo({
                top: scrollHeight,
                behavior,
              });

              if (behavior === 'auto' && retries > 0) {
                requestAnimationFrame(() => {
                  const newScrollTop = element.scrollTop;
                  const maxScroll = element.scrollHeight - element.clientHeight;

                  if (Math.abs(newScrollTop - maxScroll) > 5) {
                    performScroll();
                  }
                });
              }
            }
          }
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            performScroll();
          });
        });
      };

      scrollToBottom('auto', 3);

      let intervalId: NodeJS.Timeout | null = null;
      if (animateLastAssistant) {
        intervalId = setInterval(() => scrollToBottom('auto', 0), 200);
      }

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }, [messages, animateLastAssistant, enableScrollBehavior]);

    // Handle final scroll when typewriter completes
    React.useEffect(() => {
      if (
        !animateLastAssistant &&
        enableScrollBehavior &&
        messages.length > 0
      ) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (messagesScrollAreaRef.current) {
              const element = messagesScrollAreaRef.current;
              element.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth',
              });
            }
          });
        });
      }
    }, [animateLastAssistant, enableScrollBehavior, messages.length]);

    const handleSendMessage = async () => {
      if (!inputValue.trim() || disabled || isLoading) return;

      const messageText = inputValue;
      const filesToSend = [...selectedFiles];

      // Clear input and files
      setInputValue('');
      setSelectedFiles([]);

      try {
        if (onSendMessage) {
          await onSendMessage(messageText, filesToSend);
        }
      } catch (error) {
        console.error('Error sending message:', error);
      }
    };

    const handleFileSelect = (files: File[]) => {
      setSelectedFiles((prev: File[]) => [...prev, ...files]);
    };

    const removeFile = (index: number) => {
      setSelectedFiles((prev: File[]) =>
        prev.filter((_: File, i: number) => i !== index)
      );
    };

    React.useImperativeHandle(ref, () => ({}));

    return (
      <ChatContainer
        className={className}
        role="log"
        aria-label="Chat conversation"
      >
        <MessagesScrollArea ref={messagesScrollAreaRef}>
          <MessageList
            messages={messages}
            isLoading={isLoading}
            animateLastAssistant={animateLastAssistant}
            onTypewriterDone={onTypewriterDone}
          />
          <div ref={endRef} />
        </MessagesScrollArea>
        <StickyInputBarWrapper>
          {selectedFiles.length > 0 && (
            <FileListContainer>
              <FileChipsRow>
                {selectedFiles.map((file: File, index: number) => (
                  <FileChip key={`${file.name}-${index}`}>
                    <File size={14} color={ChatColors.filechip.TEXT} />
                    <span
                      style={{
                        fontSize: '0.875rem',
                        lineHeight: 1,
                        color: ChatColors.filechip.TEXT as unknown as string,
                      }}
                    >
                      {file.name}
                    </span>
                    <ChipRemoveButton
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.name}`}
                      data-testid={`remove-file-${index}`}
                    >
                      <X size={12} />
                    </ChipRemoveButton>
                  </FileChip>
                ))}
              </FileChipsRow>
              {/* Error message for non-PDF files */}
              {selectedFiles.some(
                (file) => !file.name.toLowerCase().endsWith('.pdf')
              ) && (
                <Box paddingTop="s">
                  <Text as="span" variant="m" color={Palette.white}>
                    Only PDF files are supported. Please remove unsupported
                    files to continue.
                  </Text>
                </Box>
              )}
            </FileListContainer>
          )}
          <InputBar
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSendMessage}
            onFileSelect={handleFileSelect}
            placeholder={placeholder}
            disabled={
              disabled ||
              isLoading ||
              selectedFiles.some(
                (file) => !file.name.toLowerCase().endsWith('.pdf')
              )
            }
            //onOpenVoiceAgent={onOpenVoiceAgent}
          />
        </StickyInputBarWrapper>
      </ChatContainer>
    );
  }
);

const MemoizedChatInterface = React.memo(
  ChatInterfaceComponent,
  (prevProps, nextProps) => {
    const shouldUpdate =
      prevProps.messages !== nextProps.messages ||
      prevProps.isLoading !== nextProps.isLoading ||
      prevProps.animateLastAssistant !== nextProps.animateLastAssistant ||
      prevProps.loadingMessage !== nextProps.loadingMessage ||
      prevProps.disabled !== nextProps.disabled ||
      prevProps.placeholder !== nextProps.placeholder ||
      prevProps.className !== nextProps.className ||
      prevProps.enableScrollBehavior !== nextProps.enableScrollBehavior;

    return !shouldUpdate;
  }
);

MemoizedChatInterface.displayName = 'MemoizedChatInterface';

export const ChatInterface = MemoizedChatInterface;
export default MemoizedChatInterface;
